import { Button } from 'react-bootstrap'
import { FaPlus, FaSave, FaTimes } from 'react-icons/fa'
import { useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable';
import CustomSelect from '../../SelectSearch/CustomSelect';
import MenuCostTable from './MenuCostTable';
import { selectStyles } from '../../../utils/cssUtils';

const readonlyClass = "col-span-2 w-full px-2 py-1.5 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed";
const editableClass = "col-span-2 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500";
const gridInputClass = "col-span-1 font-medium text-gray-700 self-center";

const MenuCostForm = ({
    handleAddCostItem,
    formData,
    handleMenuItemSelect,
    allCostItemNames,
    allMenuItemNames,
    handleClearMenuItem,
    handleInputChange,
    handleSubRecipeInputChange,
    handleSubRecipeSelect,
    handleAddSubRecipe,
    handleCancelEdit,
    handleCancelSubRecipeEdit,
    handleCostItemSelect,
    showConversionOptions,
    showSubRecipeConversionOptions,
    showSubRecipe,
    setShowSubRecipe,
    handleUomChange,
    editingIndex,
    kgVariations,
    literVariations,
    gramVariations,
    milliliterVariations,
    subRecipes,
    subRecipeFormData,
    editingSubRecipeIndex,
    menuItems,
    setAutoFill,
    autoFill,
    unitDefinitions,
    type,
    setType,
    recipeType,
    groupName,
    setGroupName,
    handleRecipeTypeChange,
    isNewMenuItem,
    costItems,
    subRecipeItems,
    handleEdit,
    handleDelete,
    handleSave,
    profitPercentage,
    handleSubRecipeDelete,
    handleSubRecipeEdit,
    manualTotalCost,
    setManualTotalCost,
    setFormData,
    setSubRecipeFormData,
    draftCostItem,
    setDraftCostItem,
    draftSubRecipeItem,
    setDraftSubRecipeItem,
    incomingRecipe,
}) => {
    const sellingPriceRef = useRef(null);
    const costItemRef = useRef(null);
    const uomRef = useRef(null);
    const quantityRef = useRef(null);
    const subRecipeRef = useRef(null);
    const subRecipeQuantityRef = useRef(null);
    const subRecipeUomRef = useRef(null);
    const menuItemRef = useRef(null);
    const hasUserEditedRef = useRef(false);
    const storedItem = localStorage.getItem('item');
    const [localCostPercent, setLocalCostPercent] = useState(30);

    const menuItemOptions = allMenuItemNames.map(name => ({ value: name, label: name }));
    const costItemOptions = allCostItemNames.map(name => ({ value: name, label: name }));
    const subRecipeOptions = subRecipes.map(recipe => ({
        value: recipe.id.toString(),
        label: recipe.name
    }));

    const itemTypeOptions = [
        { value: "Food", label: "Food" },
        { value: "Beverage", label: "Beverage" },
        { value: "Others", label: "Others" },
    ];

    const recipeTypeOptions = [
        { value: "Menu", label: "Menu" },
        { value: "Production", label: "Production" },
    ];

    useEffect(() => {
        if (storedItem) {
            setAutoFill(true);
        }

        if (storedItem && !formData.menuItem && autoFill) {
            const selectedItem = menuItems
                .flatMap(type => type.items.flatMap(category => category.items))
                .find(item => item.Name === storedItem);

            if (selectedItem) {
                handleMenuItemChange({ value: selectedItem.Name, label: selectedItem.Name });
            }
        }
    }, [formData.menuItem, menuItems]);

    const getUomOptionsWithDefault = (uom, currentNewUom) => {
        const options = [];
        const lowerUom = uom?.toLowerCase();
        let defaultOption = currentNewUom || null;

        if (kgVariations.includes(lowerUom) || gramVariations.includes(lowerUom)) {
            options.push(
                { value: 'gm', label: 'gm' },
                { value: 'kg', label: 'kg' }
            );
            if (!defaultOption) defaultOption = 'gm';
        } else if (literVariations.includes(lowerUom) || milliliterVariations.includes(lowerUom)) {
            options.push(
                { value: 'ml', label: 'ml' },
                { value: 'ltr', label: 'ltr' }
            );
            if (!defaultOption) defaultOption = 'ml';
        }

        return { options, defaultOption };
    };

    const { options: uomOptions, defaultOption: defaultUom } = getUomOptionsWithDefault(formData.uom, formData.new_uom);
    const { options: subRecipeUomOptions, defaultOption: defaultSubRecipeUom } = getUomOptionsWithDefault(subRecipeFormData.uom, subRecipeFormData.new_uom);

    useEffect(() => {
        if (uomOptions.length > 0 && !formData.new_uom) {
            handleUomChange({
                target: {
                    value: defaultUom,
                    name: "new_uom"
                }
            });
        }
    }, [uomOptions, formData.new_uom]);

    useEffect(() => {
        if (subRecipeUomOptions.length > 0 && !subRecipeFormData.new_uom) {
            handleSubRecipeInputChange({
                target: {
                    value: defaultSubRecipeUom,
                    name: "new_uom"
                }
            });
        }
    }, [subRecipeUomOptions, subRecipeFormData.new_uom]);

    useEffect(() => {
        if (!formData.menuItem) {
            hasUserEditedRef.current = false;
        }
        if (!formData.menuItem && menuItemRef.current) {
            menuItemRef.current.focus();
        } else if (formData.menuItem && !showSubRecipe && costItemRef.current) {
            costItemRef.current.focus();
        } else if (formData.menuItem && showSubRecipe && subRecipeRef.current) {
            subRecipeRef.current.focus();
        }
    }, [formData.menuItem, showSubRecipe]);

    useEffect(() => {
        if (!incomingRecipe) return;
        if (recipeType === "Production") return;
        if (hasUserEditedRef.current) return;
        const sellingPrice = parseFloat(incomingRecipe.sellingprice);
        if (!isNaN(sellingPrice) && sellingPrice > 0 && manualTotalCost > 0) {
            setLocalCostPercent(
                parseFloat(((manualTotalCost / sellingPrice) * 100).toFixed(2))
            );
        }
    }, [manualTotalCost, incomingRecipe]);

    useEffect(() => {
        if (incomingRecipe) return;
        if (recipeType === "Production") return;
        if (hasUserEditedRef.current) return;
        if (manualTotalCost <= 0) return;

        setFormData(prev => {
            const currentSellingPrice = parseFloat(prev.sellingPrice);

            if (currentSellingPrice > 0 && !isNewMenuItem) {
                setLocalCostPercent(
                    parseFloat(((manualTotalCost / currentSellingPrice) * 100).toFixed(2))
                );
                return prev;
            }

            if (!localCostPercent || localCostPercent <= 0) return prev;
            return {
                ...prev,
                sellingPrice: (manualTotalCost / (localCostPercent / 100)).toFixed(2)
            };
        });
    }, [manualTotalCost]);

    const handleMenuItemKeyDown = (e) => {
        if (e.key === 'Enter' && formData.menuItem) {
            e.preventDefault();

            if (isNewMenuItem && sellingPriceRef.current) {
                // ✅ NEW ITEM → go to selling price first
                sellingPriceRef.current.focus();
            } else if (showSubRecipe && subRecipeRef.current) {
                subRecipeRef.current.focus();
            } else if (costItemRef.current) {
                costItemRef.current.focus();
            }
        }
    };

    const handleCostItemKeyDown = (e) => {
        if (e.key === 'Enter' && formData.costItem) {
            e.preventDefault();
            if (quantityRef.current) {
                quantityRef.current.focus();
            }
        }
    };

    const handleQuantityKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (showConversionOptions && uomRef.current) {
                uomRef.current.focus();
            } else {
                handleAddCostItem(e);
                if (costItemRef.current) {
                    costItemRef.current.focus();
                }
            }
        }
    };

    const handleUomKeyDown = (e) => {
        if (e.key === 'Enter' && formData.new_uom) {
            e.preventDefault();
            handleAddCostItem(e);
            if (costItemRef.current) {
                costItemRef.current.focus();
            }
        }
    };

    const handleSubRecipeKeyDown = (e) => {
        if (e.key === 'Enter' && subRecipeFormData.subRecipe) {
            e.preventDefault();
            if (subRecipeQuantityRef.current) {
                subRecipeQuantityRef.current.focus();
            }
        }
    };

    const handleSubRecipeQuantityKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (subRecipeUomOptions.length > 0 && subRecipeUomRef.current) {
                subRecipeUomRef.current.focus();
            } else {
                handleAddSubRecipe(e);
                if (subRecipeRef.current) {
                    subRecipeRef.current.focus();
                }
            }
        }
    };

    const handleSubRecipeUomKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSubRecipe(e);
            if (subRecipeRef.current) {
                subRecipeRef.current.focus();
            }
        }
    };

    const handleMenuItemChange = (selectedOption) => {
        if (!selectedOption) return;

        const selectedItem = menuItems
            .flatMap(type => type.items.flatMap(category => category.items))
            .find(item => item.Name === selectedOption.value);

        if (selectedItem) {
            handleMenuItemSelect({
                target: {
                    value: selectedItem.Name,
                    name: "menuItem"
                }
            });
        } else {
            handleMenuItemSelect({
                target: {
                    value: selectedOption.value,
                    name: "menuItem"
                }
            });
        }
    };

    const handleCostItemChange = (selectedOption) => {
        if (!selectedOption) {
            setDraftCostItem(null);
            setFormData(prev => ({
                ...prev,
                costItem: '', rate: '', unit: '', uom: '', quantity: '', new_uom: ''
            }));
            return;
        }

        const selectedItem = unitDefinitions.find(item => item.name === selectedOption.value);

        if (selectedItem) {
            handleCostItemSelect({
                target: {
                    value: selectedItem.name,
                    name: "costItem"
                }
            });

            if (quantityRef.current) {
                setTimeout(() => {
                    quantityRef.current.focus();
                }, 100);
            }
        }
    };

    const handleUomSelectChange = (selectedOption) => {
        if (!selectedOption) return;

        handleUomChange({
            target: {
                value: selectedOption.value,
                name: "new_uom"
            }
        });
    };

    const handleSubRecipeChange = (selectedOption) => {
        if (!selectedOption) {
            setDraftSubRecipeItem(null);
            setSubRecipeFormData(prev => ({
                ...prev,
                subRecipe: '', subRecipeName: '', rate: '', unit: '', uom: '', quantity: '', new_uom: ''
            }));
            return;
        }

        const syntheticEvent = {
            target: {
                value: selectedOption.value.toString(),
                name: "subRecipe"
            }
        };

        handleSubRecipeSelect(syntheticEvent);

        if (subRecipeQuantityRef.current) {
            setTimeout(() => {
                subRecipeQuantityRef.current.focus();
            }, 100);
        }
    };

    const handleSubRecipeUomChange = (selectedOption) => {
        if (!selectedOption) return;

        handleSubRecipeInputChange({
            target: {
                value: selectedOption.value,
                name: "new_uom"
            }
        });
    };

    const handleSellingPriceChange = (e) => {
        hasUserEditedRef.current = true;
        const newPrice = e.target.value;
        setFormData(prev => ({ ...prev, sellingPrice: newPrice }));

        const parsed = parseFloat(newPrice);
        if (!isNaN(parsed) && parsed > 0) {
            setLocalCostPercent(
                parseFloat(((manualTotalCost / parsed) * 100).toFixed(2))
            );
        }
    };

    return (
        <>
            <div className="bg-white p-4 rounded-lg text-sm">
                <div className="flex items-center gap-12 mb-3">
                    <div className="flex gap-3 items-stretch">
                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap self-center">
                            Select Item
                        </label>

                        <div className="w-72">
                            <CreatableSelect
                                ref={menuItemRef}
                                options={menuItemOptions}
                                value={
                                    formData.menuItem
                                        ? { value: formData.menuItem, label: formData.menuItem }
                                        : null
                                }
                                onChange={handleMenuItemChange}
                                onCreateOption={(inputValue) => {
                                    const newOption = {
                                        value: inputValue,
                                        label: inputValue
                                    };

                                    handleMenuItemChange(newOption);
                                }}
                                placeholder="Select a menu item"
                                isDisabled={!!formData.menuItem}
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                onKeyDown={handleMenuItemKeyDown}
                                isClearable
                            />
                        </div>

                        {formData.menuItem && (
                            <Button
                                variant="danger"
                                onClick={handleClearMenuItem}
                            >
                                <FaTimes size={12} />
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={showSubRecipe}
                            onChange={() => {
                                const switching = !showSubRecipe;

                                if (switching) {
                                    if (formData.costItem) {
                                        setDraftCostItem(null);
                                        setFormData(prev => ({
                                            ...prev,
                                            costItem: '', rate: '', unit: '', uom: '', quantity: '', new_uom: ''
                                        }));
                                    }
                                } else {
                                    if (subRecipeFormData.subRecipe) {
                                        setDraftSubRecipeItem(null);
                                        setSubRecipeFormData({
                                            subRecipe: '', subRecipeName: '', quantity: '',
                                            uom: '', rate: '', unit: '', new_uom: '',
                                            link_id: '', sub_recipe_id: ''
                                        });
                                    }
                                }

                                setShowSubRecipe(switching);
                            }}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                        />
                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap"> Add Sub-Recipe</label>
                    </div>
                </div>

                <div className='grid grid-cols-7 gap-10 mb-8'>
                    <div className='col-span-2'>
                        <div className="grid grid-cols-3 gap-y-3 gap-x-1 content-start">
                            <label className={gridInputClass}>Selling Price</label>
                            <input
                                ref={sellingPriceRef}
                                type="number"
                                name="sellingPrice"
                                value={formData.sellingPrice}
                                onChange={handleSellingPriceChange}
                                className={recipeType === "Production" ? readonlyClass : editableClass}
                                disabled={recipeType === "Production"}
                            />

                            <label className={gridInputClass}>
                                Item Type
                            </label>
                            <div className="col-span-2">
                                <CustomSelect
                                    options={itemTypeOptions}
                                    value={type}
                                    onChange={(selected) => setType(selected)}
                                    placeholder="Select Item Type"
                                    isDisabled={!isNewMenuItem && !incomingRecipe}
                                />
                            </div>

                            <label className={gridInputClass}>
                                Recipe Type
                            </label>
                            <div className="col-span-2 text-sm">
                                <CustomSelect
                                    options={recipeTypeOptions}
                                    value={recipeType}
                                    onChange={(selected) => {
                                        handleRecipeTypeChange({ target: { value: selected } });
                                    }}
                                    placeholder="Select Recipe Type"
                                    isDisabled={!isNewMenuItem && !incomingRecipe}
                                />
                            </div>

                            <label className={gridInputClass}>
                                Group Name
                            </label>
                            <input
                                type="text"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                disabled={(!isNewMenuItem && !incomingRecipe) || recipeType === "Production"}
                                className={
                                    (!isNewMenuItem && !incomingRecipe) || recipeType === "Production"
                                        ? readonlyClass
                                        : editableClass
                                }
                            />

                            <hr className='col-span-3 my-0' />

                            {!showSubRecipe && (
                                <>
                                    <label className={gridInputClass}>Ingredients</label>
                                    <div className='col-span-2'>
                                        <Select
                                            ref={costItemRef}
                                            options={costItemOptions}
                                            value={formData.costItem ? { value: formData.costItem, label: formData.costItem } : null}
                                            onChange={handleCostItemChange}
                                            placeholder="Select an ingredient"
                                            required
                                            isSearchable
                                            onKeyDown={handleCostItemKeyDown}
                                            className="basic-single"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            isClearable
                                            menuPosition="fixed"
                                        />
                                    </div>

                                    <label className={gridInputClass}>
                                        Rate
                                    </label>
                                    <input
                                        type="text"
                                        name="rate"
                                        value={parseFloat(formData.rate)?.toFixed(2)}
                                        readOnly
                                        className={readonlyClass}
                                    />

                                    <label className={gridInputClass}>
                                        Unit
                                    </label>
                                    <input
                                        type="text"
                                        name="unit"
                                        value={
                                            formData.unit
                                                ? `${formData.unit} (${formData.uom})`
                                                : ''
                                        }
                                        readOnly
                                        className={readonlyClass}
                                    />

                                    <label className={gridInputClass}>
                                        Quantity
                                    </label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                        required
                                        step="any"
                                        min="0"
                                        ref={quantityRef}
                                        onKeyDown={handleQuantityKeyDown}
                                        className={editableClass}
                                    />

                                    <label className={gridInputClass}>
                                        UOM
                                    </label>

                                    {showConversionOptions && uomOptions.length > 0 ? (
                                        <div className="col-span-2">
                                            <Select
                                                ref={uomRef}
                                                options={uomOptions}
                                                value={formData.new_uom ?
                                                    uomOptions.find(option => option.value === formData.new_uom) :
                                                    (uomOptions.length > 0 ? uomOptions[0] : null)}
                                                onChange={handleUomSelectChange}
                                                placeholder="Select UOM"
                                                className="basic-single"
                                                classNamePrefix="select"
                                                menuPortalTarget={document.body}
                                                onKeyDown={handleUomKeyDown}
                                                styles={selectStyles}
                                                menuPosition="fixed"
                                                isSearchable={false}
                                            />
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            name="uom"
                                            value={formData.uom}
                                            readOnly
                                            className={readonlyClass}
                                        />
                                    )}
                                </>
                            )}

                            {showSubRecipe && (
                                <>
                                    <label className={gridInputClass}>Sub-Recipe</label>
                                    <div className='col-span-2'>
                                        <Select
                                            ref={subRecipeRef}
                                            options={subRecipeOptions}
                                            value={subRecipeFormData.subRecipe ?
                                                subRecipeOptions.find(option => option.value === subRecipeFormData.subRecipe.toString()) :
                                                null
                                            }
                                            onChange={handleSubRecipeChange}
                                            placeholder="Select a sub-recipe"
                                            onKeyDown={handleSubRecipeKeyDown}
                                            className="basic-single"
                                            classNamePrefix="select"
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            isClearable
                                            menuPosition="fixed"
                                        />
                                    </div>

                                    <label className={gridInputClass}>
                                        Rate
                                    </label>
                                    <input
                                        type="number"
                                        name="rate"
                                        value={parseFloat(subRecipeFormData.rate)?.toFixed(2)}
                                        readOnly
                                        className={readonlyClass}
                                    />

                                    <label className={gridInputClass}>
                                        Unit
                                    </label>
                                    <input
                                        type="text"
                                        name="unit"
                                        value={
                                            subRecipeFormData.unit
                                                ? `${subRecipeFormData.unit} (${subRecipeFormData.uom})`
                                                : ''
                                        }
                                        readOnly
                                        className={readonlyClass}
                                    />

                                    <label className={gridInputClass}>
                                        Quantity
                                    </label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        value={subRecipeFormData.quantity}
                                        onChange={handleSubRecipeInputChange}
                                        required
                                        step="any"
                                        min="0"
                                        ref={subRecipeQuantityRef}
                                        onKeyDown={handleSubRecipeQuantityKeyDown}
                                        className={editableClass}
                                    />

                                    <label className={gridInputClass}>
                                        UOM
                                    </label>

                                    {showSubRecipeConversionOptions && subRecipeUomOptions.length > 0 ? (
                                        <div className="col-span-2">
                                            <Select
                                                ref={subRecipeUomRef}
                                                options={subRecipeUomOptions}
                                                value={subRecipeFormData.new_uom ?
                                                    subRecipeUomOptions.find(option => option.value === subRecipeFormData.new_uom) :
                                                    (subRecipeUomOptions.length > 0 ? subRecipeUomOptions[0] : null)}
                                                onChange={handleSubRecipeUomChange}
                                                placeholder="Select UOM"
                                                className="basic-single"
                                                classNamePrefix="select"
                                                menuPortalTarget={document.body}
                                                onKeyDown={handleSubRecipeUomKeyDown}
                                                isSearchable={false}
                                                styles={selectStyles}
                                                menuPosition="fixed"
                                            />
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            name="uom"
                                            value={subRecipeFormData.uom}
                                            readOnly
                                            onKeyDown={handleSubRecipeUomKeyDown}
                                            className={editableClass}
                                        />
                                    )}
                                </>
                            )}

                            <div className="col-span-3 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={showSubRecipe ? handleAddSubRecipe : handleAddCostItem}
                                    disabled={showSubRecipe ? !subRecipeFormData.subRecipe : !formData.costItem}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                                >
                                    {(showSubRecipe
                                        ? editingSubRecipeIndex !== null
                                        : editingIndex !== null)
                                        ? <FaSave size={12} />
                                        : <FaPlus size={12} />
                                    }
                                    {(showSubRecipe
                                        ? editingSubRecipeIndex !== null
                                        : editingIndex !== null)
                                        ? "Update"
                                        : "Add"}
                                </button>

                                {(showSubRecipe
                                    ? editingSubRecipeIndex !== null
                                    : editingIndex !== null
                                ) && (
                                        <button
                                            type="button"
                                            onClick={showSubRecipe ? handleCancelSubRecipeEdit : handleCancelEdit}
                                            className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <FaTimes size={12} />
                                            Cancel
                                        </button>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className='col-span-5'>
                        <MenuCostTable
                            costItems={costItems}
                            subRecipeItems={subRecipeItems}
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}
                            handleSave={handleSave}
                            formData={formData}
                            manualTotalCost={manualTotalCost}
                            setManualTotalCost={setManualTotalCost}
                            profitPercentage={profitPercentage}
                            handleSubRecipeDelete={handleSubRecipeDelete}
                            handleSubRecipeEdit={handleSubRecipeEdit}
                            setFormData={setFormData}
                            draftCostItem={draftCostItem}
                            draftSubRecipeItem={draftSubRecipeItem}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-4 px-4">
                    <div className="flex gap-3 items-center">
                        <label className="text-sm font-medium text-gray-700">
                            Total Cost
                        </label>

                        <input
                            type="number"
                            value={manualTotalCost.toFixed(2)}
                            onChange={(e) => setManualTotalCost(e.target.value)}
                            className="h-[36px] px-3 border border-gray-300 rounded-md text-sm font-medium text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                            readOnly
                        />
                    </div>

                    <div className="flex gap-3 items-center">
                        <label className="text-sm font-medium text-gray-700">
                            Selling Price
                        </label>

                        <input
                            type="number"
                            disabled={recipeType === "Production"}
                            value={formData.sellingPrice}
                            onChange={handleSellingPriceChange}
                            className="h-[36px] px-3 border border-gray-300 rounded-md text-sm font-medium text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex gap-3 items-center">
                        <label className="text-sm font-medium text-gray-700">
                            Cost Percentage
                        </label>

                        {recipeType !== "Production" ? (
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    value={localCostPercent}
                                    onChange={(e) => {
                                        hasUserEditedRef.current = true;
                                        const val = e.target.value;
                                        if (val === "") {
                                            setLocalCostPercent("");
                                            return;
                                        }
                                        const num = parseFloat(val);
                                        if (!isNaN(num) && num > 0 && num <= 100) {
                                            setLocalCostPercent(num);
                                            if (manualTotalCost > 0) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    sellingPrice: (manualTotalCost / (num / 100)).toFixed(2)
                                                }));
                                            }
                                        }
                                    }}
                                    min="0.01"
                                    max="100"
                                    step="0.01"
                                    className="h-[36px] w-32 px-3 border border-gray-300 rounded-md text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">%</span>
                            </div>
                        ) : (
                            <input
                                type="text"
                                value={`${profitPercentage.toFixed(2)} %`}
                                readOnly
                                className="h-[36px] px-3 border border-gray-200 bg-gray-100 rounded-md text-sm font-semibold text-center text-gray-700"
                            />
                        )}
                    </div>

                    <div className='flex justify-end'>
                        <button
                            onClick={handleSave}
                            disabled={costItems.filter(i => !i.isDraft).length === 0 && subRecipeItems.filter(i => !i.isDraft).length === 0}
                            className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                        >
                            Save Cost
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default MenuCostForm